// Single source for the automation live-state chip: label, badge color, and a
// short tooltip. Shared by the builder header and the Manage-tab rows so the
// three states read identically everywhere. See docs/AUTOMATIONS-DRAFT-LIVE.md.
//
//   Off          — not running (never activated, or turned off)
//   Live         — running its last applied version, in sync with the draft
//   Live · edited — running, but the saved draft has edits not yet applied
//
// NOTE: the off state is "Off", not "Draft", on purpose — "draft" is reserved
// for the editable working copy (the "Save draft" action), so it never also
// means a status. See the doc for the reasoning.

import type { Color } from "@salestrak/ui";

import { formatDateShort } from "#app/lib/utils/format-date";
import { formatElapsedTime } from "#app/lib/utils/format-relative-date";
import type { Omittable } from "@salestrak/ui";

export type AutomationLiveStatus = Omittable<{
  label: string;
  // The chip's tone — the pastel wash with a vibrant foreground that the What's-New
  // announcement badges also carry.
  color?: Color;
  // Off only. A bordered chip (foreground text, no tone) so the neutral state
  // still reads as a distinct chip instead of washing into adjacent muted
  // text, plus a small shadow so it does not dissolve into a white row in
  // light mode.
  outline?: boolean;
  tooltip: string;
}>;

export function automationLiveStatus(
  enabled: boolean,
  // Whether an EDITING draft version exists alongside the live one — flagged
  // so a list row says "there's staged work here that isn't running".
  hasDraft: boolean,
  // When the current live version was published — enriches the Live and
  // Live · draft tooltips with "live since / how long it's been running".
  liveSince?: string | null,
  // The live version's name — folded into the live labels ("Live · Version
  // 1") so WHICH version runs reads at a glance.
  liveVersionName?: string | null,
  // The automation's SOURCE integration connection is disabled. It cannot run
  // whatever its own `enabled` flag says — the inbound webhook bails on an
  // inactive connection before dispatching — so this wins over both Off and
  // Live. Deliberately does NOT clear the stored flag: re-enabling the
  // connection restores the automation exactly as the author left it.
  connectionDisabled = false,
): AutomationLiveStatus {
  if (connectionDisabled) {
    return {
      label: "Connection disabled",
      color: "orange",
      tooltip: "Connection disabled · can't run",
    };
  }
  if (!enabled) {
    return {
      label: "Off",
      outline: true,
      tooltip: "Off · go live to start",
    };
  }
  const live = liveVersionName ? `Live · ${liveVersionName}` : "Live";
  const elapsed = liveSince ? formatElapsedTime(liveSince) : null;
  if (hasDraft) {
    return {
      label: `${live} · Draft`,
      color: "orange",
      tooltip: elapsed
        ? `Live for ${elapsed} · draft edits not live yet`
        : "Live · draft edits not live yet",
    };
  }
  return {
    label: live,
    color: "green",
    tooltip: liveSince
      ? `Live since ${formatDateShort(liveSince)} · ${elapsed}`
      : "Running on every matching entity",
  };
}

// Shared copy for the on/off toggle toast across the editor, the Manage list,
// and the workflow popover — a short title plus an optional muted description
// line (sonner's `description`) that carries the moment's facts. Turning on
// states the consequence; turning off reports how long it ran + recent activity.
export function automationToggleToast(
  enabled: boolean,
  opts: Omittable<{ liveSince?: string | null; runCount7d?: number }> = {},
  // The off branch has no description when nothing has run yet, and every
  // reader spells that `?? null` — so the member relaxes rather than forcing
  // the return to be split into "set the key" and "don't".
): Omittable<{ title: string; description?: string }> {
  if (enabled) {
    return {
      title: "Automation live",
      description: "Runs on every matching entity.",
    };
  }
  const parts: string[] = [];
  const ranFor = opts.liveSince ? formatElapsedTime(opts.liveSince) : null;
  if (ranFor) parts.push(`Ran for ${ranFor}`);
  const runs = opts.runCount7d ?? 0;
  if (runs > 0) parts.push(`${runs} ${runs === 1 ? "run" : "runs"} this week`);
  return {
    title: "Automation off",
    description: parts.length ? parts.join(" · ") : undefined,
  };
}
